import { useState, useEffect } from "react";
import "./Tags.css";
import { useCoreDataLoader } from "../../hooks/useCoreDataLoader";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { 
    selectForms,
    selectTags,
    selectUIStates,
    selectUser,
    setUIStates} from "../../redux/features/appData";
import toast from "react-hot-toast";
import { toastFail, toastSuccess } from "../../config";
import { useDispatch } from "react-redux";
import { FormExtended, iconPath, RenameTagPayload, Tag, TagExtended } from "effno-core";
import { NewTagModal } from "./NewTagModal";
import { apiCreateTagInWorkspace, apiDeleteTag, apiRenameTag } from "../../services/TagService";
import { ICONS } from "../shared/media";
import TagMenu from "./TagMenu";
import { TagDeleteModal } from "./TagDeleteModal";
import { TagRenameModal } from "./TagRenameModal";
import { Tag as TagIcon} from "lucide-react";

export default function Tags() {
    const storeUser = useSelector(selectUser);
    const storeForms = useSelector(selectForms)
    const tags = useSelector(selectTags);
    const uiStates = useSelector(selectUIStates);
    
    const { reloadAll }  = useCoreDataLoader();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [selectedTagId, setSelectedTagId] = useState<string | undefined>(undefined);
    const [deleteTagModalIsOpen, setDeleteTagModalIsOpen] = useState<boolean>(false);
    const [renameTagModalIsOpen, setRenameTagModalIsOpen] = useState<boolean>(false);
    const [newTagModalIsOpen, setNewTagModalIsOpen] = useState<boolean>(false);
    const [tagFormCounts, setTagFormCounts] = useState<{[key: string]: number} | undefined>(undefined);

    useEffect(() => {
        if (!uiStates) {
            return;
        }

        dispatch(setUIStates({
            ...uiStates,
            viewingResponses: false,
            viewingShare: false
        }));
        
    }, [])

    useEffect(() => {
        if (storeForms && tags) {

            let counts = {} as {[key: string]: number};

            storeForms.forEach((form: FormExtended) => {
                if (form.tags && form.tags.length > 0) {
                    form.tags.forEach((tag: Tag) => {
                        if (!counts[tag.id]) {
                            counts[tag.id] = 1;
                        } else {
                            counts[tag.id] = counts[tag.id] + 1;
                        }
                    })
                }
            })

            setTagFormCounts(counts);
        }
    }, [storeForms, tags])

    const handleNewTag = () => {
        setNewTagModalIsOpen(true);
    }

    const handleDeleteTag = (tagId: string) => {
        setSelectedTagId(tagId);
        setDeleteTagModalIsOpen(true);
    }

    const handleRenameTag = (tagId: string) => {
        setSelectedTagId(tagId);
        setRenameTagModalIsOpen(true);
    }

    const deleteTag = async () => {
        if (!selectedTagId || !storeUser){
            return;
        }
        
        const deletedForm = await apiDeleteTag(selectedTagId);

        if (!deletedForm) {
            toast("Failed to delete form", toastFail);
            return;
        }

        setSelectedTagId(undefined);
        reloadAll(storeUser.currentWorkspaceId);
    }

    const renameTag = async (name: string) => {
        if (!selectedTagId || !storeUser){
            return;
        }
        
        const payload = {
            tagId: selectedTagId,
            name
        } as RenameTagPayload;

        const renamedTag = await apiRenameTag(payload);

        if (!renamedTag) {
            toast("Failed to rename tag", toastFail);
            return;
        }

        setSelectedTagId(undefined);
        reloadAll(storeUser.currentWorkspaceId);
    }

    const createNewTag = async (name: string) => {
        if (!storeUser?.currentWorkspaceId) {
            return;
        }

        console.log("creating new tag: ", name);

        const payload = {
            workspaceId: storeUser.currentWorkspaceId,
            name
        }

        const tag = await apiCreateTagInWorkspace(payload);

        if (!tag) {
            toast("Failed to create tag", toastFail);
            return;
        }

        toast("Added Tag", toastSuccess);
        reloadAll(storeUser.currentWorkspaceId);
    }

    return (
        <div id="tag">
            <div className="tag-header">
                <div className="page-title">
                    Tags
                </div>

                <div className="tag-header-actions">
                    <div 
                        className="tag-header-action home-header-action-selected"
                        onClick={handleNewTag}
                    >
                        <TagIcon size={16} />
                          {/* <img src={iconPath(ICONS.TAGS, storeUser)} /> */}
                        New Tag
                    </div>
                </div>
            </div>

            <div className="tags">
                {tags && tags.map((tag: TagExtended) => {
                    return (
                        <div 
                            className="tag"
                            onClick={()=>navigate("/home/" + tag.id)}
                        >
                            <div className="tag-entry">
                                <div className="tag-title">
                                    {tag.name}
                                </div>
                                    
                                <div 
                                    className="tag-form-count"
                    
                                >
                                    {tagFormCounts && tagFormCounts[tag.id] ? 
                                            tagFormCounts[tag.id] : 0
                                    } 
                                    &nbsp;forms
                                </div>

                                <div className="tag-actions">
                                    <TagMenu 
                                        tagId={tag.id}
                                        onDelete={()=>{
                                            handleDeleteTag(tag.id)
                                        }}
                                        onRename={()=>{
                                            handleRenameTag(tag.id)
                                        }}  
                                    />
                                </div>

                            </div>   
                            
                        </div>
                        
                    )
                })
                    
                }
            </div>

            {deleteTagModalIsOpen && 
                <TagDeleteModal 
                    isOpen={deleteTagModalIsOpen}
                    onClose={() => {
                        setDeleteTagModalIsOpen(false)
                    }} 
                    onSubmit={() => {
                        setDeleteTagModalIsOpen(false)
                        deleteTag();
                    }}
                />
            }

            {renameTagModalIsOpen && 
                <TagRenameModal 
                    isOpen={renameTagModalIsOpen}
                    onClose={() => {
                        setRenameTagModalIsOpen(false)
                    }} 
                    onSubmit={(name: string) => {
                        setRenameTagModalIsOpen(false)
                        renameTag(name);
                    }}
                    defaultValue={tags?.find((tag: TagExtended) => tag.id === selectedTagId)?.name || ""}
                />
            }

            {newTagModalIsOpen && 
                <NewTagModal 
                    isOpen={newTagModalIsOpen}
                    onClose={() => {
                        setNewTagModalIsOpen(false)
                    }} 
                    onSubmit={(name: string) => {
                        setNewTagModalIsOpen(false)
                        createNewTag(name);
                    }}
                />
            }   

        </div>
        
    )
}